import { COLUMN_CARD_STATUS, CURRENCY_TYPE } from '../enum/constants';
import { IChatItem, IColumn, IFunnel, IUser } from './index';
import type { BoardCompany, BoardContact } from '../components/board-v3/board-v3.types';
import type { CardItem, CardItemsTotal } from '../components/warehouse/warehouse.types';
import type { CardPayment, DealPaymentStatus } from '../components/payments/payments.types';

export interface CardViewField {
  id: string;
  label: string;
  name?: string;
  value: any;
  type?: string;
  key?: string;
}

export interface ICardPhone {
  id: string;
  phone?: string;
  /** Newer API responses carry the normalized number here instead of `phone` */
  number_e164?: string;
}

export function getCardPhoneValue(phone: ICardPhone | null | undefined): string {
  return phone?.phone ?? phone?.number_e164 ?? '';
}

export interface ICardEmail {
  id: string;
  email: string;
}

/** Address sent in `addresses[]` of card / contact requests (Google Places compatible) */
export interface AddressInput {
  /** ISO 3166-1 alpha-2, the only required field */
  country_code: string;
  region?: string | null;
  district?: string | null;
  city?: string | null;
  street?: string | null;
  building?: string | null;
  apartment?: string | null;
  postal_code?: string | null;
  formatted_address?: string | null;
  place_id?: string | null;
  /** Sent only together with `longitude` */
  latitude?: number | null;
  longitude?: number | null;
}

export type AddressType = 'home' | 'work' | 'other';

/** Address of a card, a contact or a company; `id` changes after every save of the list */
export interface IAddress extends AddressInput {
  id: string;
  company_id?: string;
  card_id?: string;
  contact_id?: string;
  company_v2_id?: string;
  type?: AddressType;
  is_primary?: boolean;
  active?: boolean;
  created_at?: string;
}

export interface ICardColor {
  background: string;
  text: string;
}

export interface CreateCardReminderRequest {
  title: string;
  text?: string;
  due_at: string;
  assigned_id?: string;
  reminders?: string[];
}

export interface ICardV2 {
  id: string;
  parent_id?: string | null;
  company_id: string;
  funnel_id: string;
  column_id: string;
  contact_id: string;
  assigned_id: string;
  name: string;
  currency: CURRENCY_TYPE;
  price: number;
  status: COLUMN_CARD_STATUS;
  position: number;
  description: string;
  color: ICardColor;
  active: boolean;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  comment: string;
  custom_attributes: Record<string, any>;
  custom_fields: Record<string, any>;
  x_data: Record<string, any>;
  created_user?: IUser;
  assigned?: IUser | null;
  assigned_name?: string | null;
  assigned_to_id?: string | null;
  selected?: boolean;
  _visibleFields?: CardViewField[];


  column: IColumn;
  funnel: IFunnel;
  phones: ICardPhone[];
  emails: ICardEmail[];
  addresses?: IAddress[];
  chats?: IChatItem[];
  contacts?: BoardContact[];
  /** A card has at most one company */
  company?: BoardCompany | null;
  /** Product items of the card, as GET /board/card/:card_id/item returns them */
  items?: CardItem[];
  /** Sum of the item amounts of every currency */
  items_totals?: CardItemsTotal[];
  /** The items are written off by the card status and are locked until it goes back */
  items_written_off?: boolean;
  /** `price` is the sum of the product items in the card currency, recalculated by the backend */
  is_price_from_items?: boolean;
  /** UTM tags of the lead source, set by integrations (e.g. Ringostat) on creation and editable by hand */
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  /** Payments of the card, canceled ones too, from old to new; every payments request returns them with the totals */
  payments?: CardPayment[];
  /** Sum of the paid payments, the card `price` is what it is compared with */
  paid_total?: number;
  payment_status?: DealPaymentStatus;
  sections: any[];

  editable: boolean;
}
